import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Button, message, Input, Alert } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import FormPasswordInput from '../components/global/Forms/FormPasswordInput';
import { resetPassword, resetPasswordWithCode } from '../actions/user';
import LoginFooter from './LoginFooter';

type ResetPasswordProps = {
  token?: string;
  email?: string;
  code?: string;
};

interface FormValues {
  password: string;
  passwordConfirm: string;
}

const initialValues: FormValues = {
  password: '',
  passwordConfirm: '',
};

const ResetPasswordPage: React.FC<ResetPasswordProps> = ({ token, email, code }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState(code || '');

  const isCognitoFlow = !!(email && code);
  const isMySQLFlow = !!token && !isCognitoFlow;

  const getValidationSchema = () => {
    return Yup.object().shape({
      password: Yup.string()
        .required(t('validation:required'))
        .min(12, t('validation:passwordLength', { length: 12 }))
        .matches(/[0-9]/, t('validation:passwordRequiresNumber'))
        .matches(/[a-z]/, t('validation:passwordRequiresLowercase'))
        .matches(/[A-Z]/, t('validation:passwordRequiresUppercase'))
        .matches(/[^\w]/, t('validation:passwordRequiresSpecial')),
      passwordConfirm: Yup.string()
        .oneOf([Yup.ref('password')], t('validation:passwordsDontMatch'))
        .required(t('validation:required')),
    });
  };

  const formMethods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(getValidationSchema()),
    mode: 'onTouched',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const onSubmit = async (values: FormValues) => {
    setError(null);

    try {
      if (isCognitoFlow) {
        // @ts-ignore
        await dispatch(resetPasswordWithCode(email, verificationCode, values.password));
      } else if (isMySQLFlow && token) {
        // @ts-ignore
        await dispatch(resetPassword(token, values.password));
      } else {
        setError(t('login:invalidResetLink'));
        return;
      }

      message.success(t('login:passwordResetSuccess'));
      setSubmitted(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err: any) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'invalid_code') {
        setError(t('login:invalidVerificationCode'));
      } else if (errorCode === 'invalid_password') {
        setError(t('login:invalidPasswordRequirements'));
      } else {
        setError(t('login:passwordResetFailed'));
      }
    }
  };

  if (!token && !email) {
    return (
      <div className="login__background">
        <div className="login__box">
          <div className="login__box-header">
            <div className="login-logo" />
          </div>
          <div className="login__box-content">
            <div className="login--header-content">
              <div className="login__title">{t('login:resetPassword')}</div>
            </div>
            <Alert type="error" showIcon message={t('login:invalidResetLink')} />
            <div className="text-center mt-5">
              <Link className="register__login-text" to="/">
                {t('login:backToLogin')}
              </Link>
            </div>
          </div>
        </div>
        <LoginFooter />
      </div>
    );
  }

  return (
    <div className="login__background">
      <div className="login__box">
        <div className="login__box-header">
          <div className="login-logo" />
        </div>
        <div className="login__box-content">
          <div className="login--header-content">
            <div className="login__title">{t('login:resetPassword')}</div>
            <div>{t('login:createNewPassword')}</div>
          </div>

          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}

          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {isCognitoFlow && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">{t('common:email')}</label>
                    <Input
                      prefix={<MailOutlined />}
                      size="large"
                      value={email}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">
                      {t('login:verificationCode')}
                    </label>
                    <Input
                      prefix={<LockOutlined />}
                      size="large"
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value)}
                      placeholder={t('login:verificationCodePlaceholder')}
                      disabled={submitted}
                      maxLength={6}
                    />
                  </div>
                </>
              )}
              <FormPasswordInput
                disabled={submitted}
                name="password"
                placeholder={t('common:password')}
                required
                size="large"
                useHookForm
              />
              <FormPasswordInput
                disabled={submitted}
                name="passwordConfirm"
                placeholder={t('login:confirmPassword')}
                size="large"
                required
                useHookForm
              />
              <div className="whitespace-pre-line">{t('user:passwordInfo')}</div>
              <Button
                type="primary"
                htmlType="submit"
                disabled={isSubmitting || submitted || (isCognitoFlow && !verificationCode)}
                loading={isSubmitting}
                style={{ width: '100%', marginTop: 15, marginBottom: 10 }}
                size="large"
              >
                {t('common:confirm')}
              </Button>
            </form>
          </FormProvider>
          <div className="text-center mt-5">
            <Link className="register__login-text" to="/">
              {t('login:backToLogin')}
            </Link>
          </div>
        </div>
      </div>
      <LoginFooter />
    </div>
  );
};

export default ResetPasswordPage;
